import React, { useCallback, useEffect, useMemo, useReducer, createContext, useContext as useContext$1, useState } from 'react';
import { set, get } from 'idb-keyval';

const STORE_EVENT_PREFIX = 'store:';
const ANALYTICS_EVENT_TYPE = 'AnalyticsEvent';
const wrap = event => ({
  name: ANALYTICS_EVENT_TYPE,
  params: { ...event,
    name: `${STORE_EVENT_PREFIX}${event.name}`
  }
});
const unwrap = event => {
  return { ...event.params,
    name: event.params.name.slice(STORE_EVENT_PREFIX.length, event.params.name.length)
  };
};

const sendAnalyticsEvent = event => {
  try {
    window.postMessage(wrap(event), window.origin);
  } catch (e) {
    // IE and Edge have a bug on postMessage inside promises.
    // Ignoring for now, will try to find a workaround that
    // makes postMessage work on those browsers.
    // https://developer.microsoft.com/en-us/microsoft-edge/platform/issues/14719328/
    console.error(e);
  }
};

const useAnalyticsEvent = handler => {
  const callback = useCallback(message => {
    try {
      if (message.data.name !== ANALYTICS_EVENT_TYPE) {
        return;
      }

      handler(unwrap(message.data));
    } catch (err) {
      console.error('Something went wrong while running Analytics handler');
    }
  }, [handler]);
  useEffect(() => {
    window.addEventListener('message', callback);
    return () => window.removeEventListener('message', callback);
  }, [callback]);
};

class SDKError extends Error {
  constructor(message) {
    super(`[sdk]: ${message}`);
  }

}

const sortKeys = /*#__PURE__*/new Set(['price_desc', 'price_asc', 'orders_desc', 'name_desc', 'name_asc', 'release_desc', 'discount_desc', 'score_desc']);
const initialize = ({
  sort = 'score_desc',
  selectedFacets = [],
  term = null,
  base = '/',
  page = 0
} = {}) => ({
  sort,
  selectedFacets,
  term,
  base: base.endsWith('/') ? base : `${base}/`,
  page
});

const isSearchSort = x => sortKeys.has(x);

const equals = (s1, s2) => format(s1).href === format(s2).href;

const removeFacet = (state, facet) => {
  const {
    value
  } = facet;
  const index = state.selectedFacets.findIndex(x => x.value === value);

  if (index < 0) {
    throw new SDKError(`Cannot remove ${value} from search params`);
  } // We can't allow removing the first facet, otherwise we would loose
  // the navigation context
  //
  // TODO: Remove returning the base selected facets to the frontend since
  // we won't be unselecting it anyways


  return { ...state,
    selectedFacets: state.selectedFacets.filter((_, it) => it === 0 || it !== index)
  };
};

const setFacet = (state, facet, unique) => {
  if (unique === true) {
    const index = state.selectedFacets.findIndex(f => f.key === facet.key);

    if (index > -1) {
      return { ...state,
        selectedFacets: state.selectedFacets.map((f, it) => it === index ? facet : f)
      };
    }
  }

  return { ...state,
    selectedFacets: [...state.selectedFacets, facet]
  };
};

const toggleFacet = (state, item) => {
  const found = state.selectedFacets.find(facet => facet.key === item.key && facet.value === item.value);

  if (found !== undefined) {
    return removeFacet(state, item);
  }

  return setFacet(state, item, false);
};

const toggleFacets = (state, items) => items.reduce((item, s) => toggleFacet(item, s), state);

const reducer = (state, action) => {
  switch (action.type) {
    case 'setSort':
      if (!isSearchSort(action.payload)) {
        throw new SDKError(`Sort param ${action.payload} is unknown`);
      }

      return state.sort === action.payload ? state : { ...state,
        sort: action.payload
      };

    case 'setTerm':
      return state.term === action.payload ? state : { ...state,
        term: action.payload
      };

    case 'setPage':
      return state.page === action.payload ? state : { ...state,
        page: action.payload
      };

    case 'setFacet':
      return setFacet(state, action.payload.facet, action.payload.unique);

    case 'setFacets':
      return state.selectedFacets !== action.payload ? { ...state,
        selectedFacets: action.payload
      } : state;

    case 'removeFacet':
      return removeFacet(state, action.payload);

    case 'toggleFacet':
      return toggleFacet(state, action.payload);

    case 'toggleFacets':
      return toggleFacets(state, action.payload);

    default:
      throw new SDKError(`Unknown action of search state machine`);
  }
};

const dispatcher = (onChange, state) => action => {
  const newState = reducer(state, action);

  if (!equals(newState, state)) {
    onChange(format(newState));
  }
};

const useSearchState = (initialState, onChange) => {
  const state = useMemo(() => initialize(initialState), [initialState]);
  return useMemo(() => {
    const dispatch = dispatcher(onChange, state);
    return {
      state,
      setSort: sort => dispatch({
        type: 'setSort',
        payload: sort
      }),
      setTerm: term => dispatch({
        type: 'setTerm',
        payload: term
      }),
      setPage: page => dispatch({
        type: 'setPage',
        payload: page
      }),
      setFacet: (facet, unique = false) => dispatch({
        type: 'setFacet',
        payload: {
          facet,
          unique
        }
      }),
      setFacets: facets => dispatch({
        type: 'setFacets',
        payload: facets
      }),
      removeFacet: facet => dispatch({
        type: 'removeFacet',
        payload: facet
      }),
      toggleFacet: facet => dispatch({
        type: 'toggleFacet',
        payload: facet
      }),
      toggleFacets: facets => dispatch({
        type: 'toggleFacets',
        payload: facets
      })
    };
  }, [onChange, state]);
};

const format = params => {
  const url = new URL(params.base, 'http://localhost');
  const {
    page,
    selectedFacets,
    sort,
    term
  } = params;

  if (term !== null) {
    url.searchParams.set('q', term);
  }

  const facets = new Set();

  for (const facet of selectedFacets) {
    url.searchParams.append(facet.key, facet.value);
    facets.add(facet.key);
  }

  if (selectedFacets.length > 0) {
    url.searchParams.set('facets', Array.from(facets).join(','));
  }

  url.searchParams.set('sort', sort);
  url.searchParams.set('page', page.toString());
  return url;
};
const parse = ({
  pathname,
  searchParams
}) => {
  var _searchParams$get, _searchParams$get2, _searchParams$get3, _searchParams$get$spl, _searchParams$get4;

  let state = initialize({
    base: pathname,
    term: (_searchParams$get = searchParams.get('q')) != null ? _searchParams$get : null,
    sort: (_searchParams$get2 = searchParams.get('sort')) != null ? _searchParams$get2 : undefined,
    page: Number((_searchParams$get3 = searchParams.get('page')) != null ? _searchParams$get3 : 0)
  });
  const facets = (_searchParams$get$spl = (_searchParams$get4 = searchParams.get('facets')) == null ? void 0 : _searchParams$get4.split(',')) != null ? _searchParams$get$spl : [];

  for (const facet of facets) {
    const values = searchParams.getAll(facet);

    for (const value of values) {
      state = reducer(state, {
        type: 'setFacet',
        payload: {
          facet: {
            key: facet,
            value
          },
          unique: false
        }
      });
    }
  }

  return state;
};

/* eslint-disable no-case-declarations */

const reducer$1 = (state, action) => {
  switch (action.type) {
    case 'addPrev':
      const prev = state[0] - 1;
      return [prev, ...state];

    case 'addNext':
      const next = Number(state[state.length - 1]) + 1;
      return [...state, next];

    default:
      throw new SDKError('Unknown action for infinite search');
  }
};

const useSearchInfiniteState = initialPage => {
  const [pages, dispatch] = useReducer(reducer$1, initialPage, () => [initialPage]);
  const actions = useMemo(() => ({
    addPrevPage: () => dispatch({
      type: 'addPrev'
    }),
    addNextPage: () => dispatch({
      type: 'addNext'
    })
  }), []);
  return {
    pages,
    ...actions
  };
};

const Context = /*#__PURE__*/createContext(undefined);
const Provider = ({
  children,
  itemsPerPage,
  onChange,
  ...rest
}) => {
  const {
    state,
    ...searchActions
  } = useSearchState(rest, onChange);
  const {
    pages,
    ...infiniteActions
  } = useSearchInfiniteState(state.page);
  const value = useMemo(() => ({
    state,
    ...searchActions,
    pages,
    ...infiniteActions,
    itemsPerPage
  }), [infiniteActions, itemsPerPage, pages, searchActions, state]);
  return React.createElement(Context.Provider, {
    value: value
  }, children);
};

/**
 * @description Like React.useContext but throws when the context's value === undefined.
 * This is useful when you want to force the context to be present in the React's tree before using it
 */

const useContext = context => {
  var _context$displayName;

  const value = useContext$1(context);

  if (value !== undefined) {
    return value;
  }

  throw new SDKError(`${(_context$displayName = context.displayName) != null ? _context$displayName : 'Context'} needs to be on the React tree`);
};

const useSearch = () => useContext(Context);

const getLink = state => {
  const {
    pathname,
    search
  } = format(state);
  return `${pathname}${search}`;
};

const usePagination = totalItems => {
  const {
    pages,
    itemsPerPage,
    state
  } = useSearch();
  const total = Math.ceil(totalItems / itemsPerPage);
  const next = Number(pages[pages.length - 1]) + 1;
  const prev = pages[0] - 1;
  return useMemo(() => ({
    next: next < total && {
      cursor: next,
      link: getLink({ ...state,
        page: next
      })
    },
    prev: prev > -1 && {
      cursor: prev,
      link: getLink({ ...state,
        page: prev
      })
    }
  }), [next, prev, state, total]);
};

const Context$1 = /*#__PURE__*/createContext(undefined);
Context$1.displayName = 'UIContext';
const baseInitialState = {
  displayMinicart: false
};

const reducer$2 = actions => {
  const allActions = {
    OPEN_MINICART: state => ({ ...state,
      displayMinicart: true
    }),
    CLOSE_MINICART: state => ({ ...state,
      displayMinicart: false
    }),
    ...actions
  };
  return (state, {
    type,
    data
  }) => {
    const maybeAction = allActions[type];

    if (typeof maybeAction === 'function') {
      return maybeAction(state, data);
    }

    throw new Error('Unknown UI state');
  };
};

const defaultEffects = () => ({});

const Provider$1 = ({
  children,
  actions = {},
  effects = defaultEffects,
  initialState = {}
}) => {
  const [state, dispatch] = useReducer(reducer$2(actions), { ...baseInitialState,
    ...initialState
  });
  const effectsValue = useMemo(() => ({ ...effects(dispatch),
    openMinicart: () => dispatch({
      type: 'OPEN_MINICART'
    }),
    closeMinicart: () => dispatch({
      type: 'CLOSE_MINICART'
    })
  }), [effects]);
  const value = useMemo(() => ({ ...state,
    ...effectsValue
  }), [effectsValue, state]);
  return React.createElement(Context$1.Provider, {
    value: value
  }, children);
};

const useGlobalUIState = () => useContext(Context$1);

/**
 * Safe IDB storage interface. These try..catch are useful because
 * some browsers may block access to these APIs due to security policies
 *
 * Also, the stored value is lazy-loaded to avoid hydration mismatch
 * between server/browser. When state is 'hydrated', the value in the heap
 * is the same as the value in IDB
 */

const getItem = async key => {
  try {
    const value = await get(key);
    return value != null ? value : null;
  } catch (err) {
    return null;
  }
};

const setItem = async (key, value) => {
  try {
    await set(key, value);
  } catch (err) {// noop
  }
};

const useStorage = (key, initialValue) => {
  const [init] = useState(initialValue);
  const [data, setData] = useState(init);
  useEffect(() => {
    // Avoids race condition between this and next effect hook.
    if (data !== init) {
      setItem(key, data);
    }
  }, [data, init, key]);
  useEffect(() => {
    let cancel = false;

    const effect = async () => {
      const item = await getItem(key);

      if (!cancel && item !== null) {
        setData(item);
      }
    };

    const focusHandler = () => {
      if (document.visibilityState === 'visible') {
        effect();
      }
    };

    setTimeout(effect, 0);
    document.addEventListener('visibilitychange', focusHandler);
    window.addEventListener('focus', focusHandler);
    return () => {
      cancel = true;
      document.removeEventListener('visibilitychange', focusHandler);
      window.removeEventListener('focus', focusHandler);
    };
  }, [key]);
  return [data, setData];
};

const Context$2 = /*#__PURE__*/createContext(undefined);
Context$2.displayName = 'StoreSessionContext';
const baseInitialState$1 = {
  currency: {
    code: 'USD',
    symbol: '$'
  },
  country: 'USA',
  locale: 'en',
  postalCode: null,
  channel: null,
  user: null
};
const Provider$2 = ({
  children,
  initialState,
  namespace = 'main'
}) => {
  const [session, setSession] = useStorage(`${namespace}::store::session`, () => ({ ...baseInitialState$1,
    ...initialState
  }));
  const value = useMemo(() => ({ ...session,
    setSession: data => setSession({ ...session,
      ...data
    })
  }), [session, setSession]);
  return React.createElement(Context$2.Provider, {
    value: value
  }, children);
};

const useSession = () => useContext(Context$2);

const Context$3 = /*#__PURE__*/createContext(undefined);
Context$3.displayName = 'StoreCartContext';
const initialState = {
  id: '',
  items: {}
};

const setItemToCartState = (cart, id, item) => ({ ...cart,
  items: { ...cart.items,
    [id]: item
  }
});

const removeItemFromCartState = (cart, id) => {
  const {
    [id]: removed,
    ...items
  } = cart.items;
  return { ...cart,
    items
  };
};

const CartProvider = ({
  children,
  onItemAdd,
  initialCart,
  onItemRemove,
  onItemUpdate,
  namespace: nspc = 'main'
}) => {
  const namespace = `${nspc}::store::cart`;
  const [cart, setCart] = useStorage(namespace, () => ({ ...initialState,
    ...initialCart
  }));
  const value = useMemo(() => {
    const items = Object.values(cart.items);

    const set = c => {
      const newCart = { ...c,
        items: c.items.reduce((acc, curr) => {
          acc[curr.id] = curr;
          return acc;
        }, {})
      };
      setCart(newCart);
    };

    const addItem = item => {
      if (!item.id) {
        throw new SDKError('You must provide an `id` for items');
      }

      if (item.quantity < 0) {
        throw new SDKError('Item quantity needs to be higher than zero');
      }

      const currentItem = cart.items[item.id];
      const newItem = currentItem ? { ...item,
        quantity: currentItem.quantity + item.quantity
      } : item;
      const callback = currentItem ? onItemUpdate : onItemAdd;
      setCart(setItemToCartState(cart, newItem.id, newItem));
      callback == null ? void 0 : callback(newItem, currentItem);
    };

    const updateItemQuantity = (id, quantity) => {
      const currentItem = cart.items[id];

      if (!currentItem) {
        throw new SDKError(`Item with id not found: ${id}`);
      }

      const newItem = { ...currentItem,
        quantity
      };
      setCart(setItemToCartState(cart, id, newItem));
      onItemUpdate == null ? void 0 : onItemUpdate(newItem, currentItem);
    };

    const removeItem = id => {
      const item = cart.items[id];
      setCart(removeItemFromCartState(cart, id));
      onItemRemove == null ? void 0 : onItemRemove(item);
    };

    const emptyCart = () => setCart({ ...cart,
      items: {}
    });

    const getItem = id => cart.items[id];

    const inCart = id => Boolean(cart.items[id]);

    return { ...cart,
      setCart: set,
      addItem,
      updateItemQuantity,
      removeItem,
      emptyCart,
      getItem,
      inCart,
      items,
      isEmpty: items.length === 0
    };
  }, [cart, onItemAdd, onItemRemove, onItemUpdate, setCart]);
  return React.createElement(Context$3.Provider, {
    value: value
  }, children);
};

const Context$4 = /*#__PURE__*/createContext(undefined);
Context$4.displayName = 'StoreCartValidatorContext';

const nullable = async () => null; // Validation queue


let queue = /*#__PURE__*/Promise.resolve();
const OptimisticProvider = ({
  children,
  onValidateCart = nullable
}) => {
  const {
    items,
    id,
    setCart
  } = useContext(Context$3);
  const cart = useMemo(() => ({
    id,
    items
  }), [id, items]);
  const [isValidating, setIsValidating] = useState(false);
  useEffect(() => {
    let cancel = false;

    const revalidate = async () => {
      if (cancel) {
        return;
      }

      setIsValidating(true);
      const newCart = await onValidateCart(cart);

      if (cancel) {
        return;
      }

      setIsValidating(false);

      if (newCart != null) {
        setCart(newCart);
      }
    }; // Enqueue validation


    setTimeout(() => {
      queue = queue.then(revalidate);
    }, 0);
    return () => {
      cancel = true;
    };
  }, [cart, onValidateCart, setCart]);
  return React.createElement(Context$4.Provider, {
    value: isValidating
  }, children);
};

const Provider$3 = ({
  mode = 'pure',
  onValidateCart,
  children,
  ...cartProps
}) => React.createElement(CartProvider, Object.assign({}, cartProps), mode === 'optimistic' ? React.createElement(OptimisticProvider, {
  onValidateCart: onValidateCart
}, children) : children);

const useCart = () => {
  const cart = useContext(Context$3);
  const optimistic = React.useContext(Context$4);
  return useMemo(() => ({ ...cart,
    isValidating: Boolean(optimistic)
  }), [cart, optimistic]);
};

export { ANALYTICS_EVENT_TYPE, Provider$3 as CartProvider, STORE_EVENT_PREFIX, Provider as SearchProvider, Context$2 as SessionContext, Provider$2 as SessionProvider, Context$1 as UIContext, Provider$1 as UIProvider, format as formatSearchState, initialize as initSearchState, parse as parseSearchState, sendAnalyticsEvent, useAnalyticsEvent, useCart, useGlobalUIState, usePagination, useSearch, useSession, useStorage };
//# sourceMappingURL=sdk.esm.js.map
